const nextHeaders = import('next/headers');
import { getCookie } from 'cookies-next/client';

import { isServerCheck } from '../env/isServerCheck';
import { replacePathParams } from '../http/replacePathParams';
import { type RequestBody } from '../http/type-helpers';

import {
  COLLAGE_RECOMMENDED_ITEMS_LIMIT,
  MAX_CUTOUT_ATTEMPTS,
} from './constants';
import {
  type AllowedGender,
  type AllowedSeason,
  type CollageRecommendedItems,
  type CollageRecommendedItemsArgs,
  type FeedFiltersResponse,
  type Outfit,
  type PaginatedOutfits,
} from './types';

import { ACCESS_TOKEN_KEY } from '@/constants/cookies';
import {
  CREATE_OUTFIT_ENDPOINT,
  OUTFITS_CONFIG_ENDPOINT,
  PAGINATED_OUTFITS_ENDPOINT,
  SINGLE_OUTFIT_ENDPOINT,
  UPDATE_OUTFIT_ENDPOINT,
  DELETE_BATCH_OUTFITS_ENDPOINT,
  LIKE_OUTFIT_ENDPOINT,
  COLLAGE_ITEMS,
  COLLAGE_PRODUCTS_CATEGORIES,
  COLLAGE_CUTOUTS_ENDPOINT,
  OUTFITS_USER_CONFIG_ENDPOINT,
} from '@/constants/endpoints';
import { X_COUNTRY_CODE } from '@/constants/headers';
import http, { typedHttp } from '@/modules/http';
import { type AxiosCompatibleResponse } from '@/modules/http/types';

export async function getSingleOutfit(outfitId: string) {
  // TODO(typedhttp): migrate getSingleOutfit to typedHttp.get — the
  // presentation API spec for the outfit detail/list endpoints is loose/incorrect
  // (picture `formats` typed as `number`, `tags` as `number`, `products` optional).
  // eslint-disable-next-line local-rules/use-typed-http
  return http.get<Outfit>(
    SINGLE_OUTFIT_ENDPOINT.replace(':outfitId', outfitId),
    {
      withAuth: true,
      headers: {
        'user-agent': isServerCheck()
          ? (await (await nextHeaders).headers()).get('user-agent') ||
            'Unknown UA'
          : navigator.userAgent,
      },
    }
  );
}

export function getPaginatedOutfits(params: {
  after: string;
  limit?: number;
  createdBy?: number;
  sortBy?: string;
}) {
  // TODO(typedhttp): PaginatedOutfits api spec doesn't match expected type,
  // update and migrate to typedHttp
  // eslint-disable-next-line local-rules/use-typed-http
  return http.get<PaginatedOutfits>(PAGINATED_OUTFITS_ENDPOINT, {
    withAuth: true,
    params: {
      after: params.after?.length ? params.after : undefined,
      limit: params.limit,
      created_by: params.createdBy,
      sort_by: params.sortBy,
    },
  });
}

export function createOutfit(
  params: RequestBody<typeof CREATE_OUTFIT_ENDPOINT>
) {
  return typedHttp.post(CREATE_OUTFIT_ENDPOINT, params, {
    withAuth: true,
  });
}

export function updateOutfit(
  params: RequestBody<typeof UPDATE_OUTFIT_ENDPOINT, 'put'> & {
    outfitId: string;
  }
) {
  const { outfitId, ...rest } = params;
  return typedHttp.put(
    replacePathParams(UPDATE_OUTFIT_ENDPOINT, { outfitId }),
    rest,
    {
      withAuth: true,
    }
  );
}

export function getOutfitsConfig() {
  return typedHttp.get(OUTFITS_CONFIG_ENDPOINT, { withAuth: true });
}

export function getOutfitsUserConfig(userId: string) {
  return typedHttp.get(
    replacePathParams(OUTFITS_USER_CONFIG_ENDPOINT, { userId })
  );
}

export function deleteOutfit(outfitId: string) {
  // TODO(typedhttp): align the OpenAPI spec so this can use typedHttp. The v1
  // /outfits/{outfitId}/ path only defines GET (delete is `never`), so there is
  // no delete operation to infer a response type from.
  // eslint-disable-next-line local-rules/use-typed-http
  return http.delete(SINGLE_OUTFIT_ENDPOINT.replace(':outfitId', outfitId), {
    withAuth: true,
  });
}

export function deleteBatchOutfits(outfitIds: string[]) {
  return http.post(
    DELETE_BATCH_OUTFITS_ENDPOINT,
    {
      outfit_ids: outfitIds,
    },
    {
      withAuth: true,
    }
  );
}

export function likeOutfit(outfitId: string) {
  return typedHttp.put(
    replacePathParams(LIKE_OUTFIT_ENDPOINT, { outfitId }),
    null,
    { withAuth: true }
  );
}

export function unlikeOutfit(outfitId: string) {
  return typedHttp.delete(
    replacePathParams(LIKE_OUTFIT_ENDPOINT, { outfitId }),
    {
      withAuth: true,
    }
  );
}

export type CollageCutoutFormat = {
  height: number;
  width: number;
  url: string;
};

export type CollageCutoutResponse = {
  cutout: {
    id: number;
    formats: Record<string, CollageCutoutFormat>;
    is_square: boolean;
  };
};

export async function getCollageCutout(
  productId: number,
  pictureId?: number
): Promise<CollageCutoutResponse | null> {
  let attempt = 0;

  while (attempt < MAX_CUTOUT_ATTEMPTS) {
    attempt++;

    const authToken = getCookie(ACCESS_TOKEN_KEY);

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      try {
        const response = await fetch(COLLAGE_CUTOUTS_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
          body: JSON.stringify({
            product_id: productId,
            ...(pictureId && { picture_id: pictureId }),
          }),
          signal: controller.signal,
        });

        if (response.status >= 400) {
          throw new Error('Cannot get cutout');
        }

        return await response.json();
      } finally {
        clearTimeout(timeout);
      }
    } catch {
      if (attempt === MAX_CUTOUT_ATTEMPTS) {
        return null;
      }
    }
  }

  return null;
}

export async function getCollageItems({
  category,
  cursor,
  filter,
  countryCode,
}: CollageRecommendedItemsArgs): Promise<
  AxiosCompatibleResponse<CollageRecommendedItems>
> {
  const queryParams = new URLSearchParams([
    ['limit', String(COLLAGE_RECOMMENDED_ITEMS_LIMIT)],
  ]);

  if (cursor) {
    queryParams.append('cursor', String(cursor));
  }

  if (filter) {
    const filterParams = new URLSearchParams(filter);
    filterParams.forEach((value, key) => {
      queryParams.append(key, value);
    });
  }

  const queryString = queryParams.toString();

  return http.get(
    `${COLLAGE_ITEMS.replace(':category', category)}?${queryString}`,
    {
      withAuth: true,
      headers: {
        [X_COUNTRY_CODE]: countryCode,
      },
    }
  );
}

export async function getCollageProductCategories() {
  return typedHttp.get(COLLAGE_PRODUCTS_CATEGORIES, {
    withAuth: true,
  });
}

export async function getOutfitsFeedFilters() {
  const headers = (await (await nextHeaders).headers?.()) as Headers | null;
  const country =
    headers?.get('x-country-code') || headers?.get('X-Country-Code') || 'GB';

  // TODO(typedhttp): replace the hardcoded path with the feed-filters endpoint
  // constant and migrate to typedHttp.get once the outfit spec is corrected.
  // eslint-disable-next-line local-rules/use-typed-http
  return http.get<FeedFiltersResponse>(
    '/presentation/api/v1/outfits/feed/filters/',
    {
      withAuth: true,
      headers: { [X_COUNTRY_CODE]: country },
    }
  );
}

export function getModerationOutfits(params: {
  after: string;
  limit?: number;
  [key: string]: string | number | boolean | null | undefined;
}): Promise<AxiosCompatibleResponse<PaginatedOutfits>> {
  // Extract known params that need special handling
  const { after, limit, gender, season, tag: style, sort, ...rest } = params;

  const genderSafe: AllowedGender = ((): AllowedGender => {
    const g = (gender || 'all').toString().toLowerCase();
    return g === 'men' || g === 'women' || g === 'unisex' || g === 'all'
      ? (g as AllowedGender)
      : 'all';
  })();

  const seasonSafe: AllowedSeason | undefined = (():
    AllowedSeason | undefined => {
    let s = (season || '').toString().toLowerCase();
    if (s === 'fall') {
      s = 'autumn';
    }
    return s === 'spring' || s === 'summer' || s === 'autumn' || s === 'winter'
      ? (s as AllowedSeason)
      : undefined;
  })();

  const styleSafe = (style || '').toString().toLowerCase().trim() || undefined;
  const tag =
    [genderSafe !== 'all' ? genderSafe : undefined, seasonSafe, styleSafe]
      .filter(Boolean)
      .join(',') || undefined;

  const apiParams: Record<string, string | number | boolean | undefined> = {
    after: after?.toString().length ? after.toString() : undefined,
    limit: typeof limit === 'number' ? limit : 18,
    sort_by: sort?.toString(),
    tag,
  };

  // Pass through all other params directly
  Object.entries(rest).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    if (value === 'true') {
      apiParams[key] = true;
      return;
    }

    if (value === 'false') {
      apiParams[key] = false;
      return;
    }

    apiParams[key] = value;
  });

  // TODO(typedhttp): PaginatedOutfits response object type doesn't match
  // the OpenAPI spec for this endpoint, align and use typedHttp here
  // eslint-disable-next-line local-rules/use-typed-http
  return http.get(PAGINATED_OUTFITS_ENDPOINT, {
    withAuth: true,
    params: apiParams,
  });
}
